/**
 * SitePerformance — "How are we doing on site?" analytics block.
 *
 * Role-aware:
 *   • admin    → full dashboard: KPIs + Site vs HQ time + WO
 *                throughput + hours per customer + avg completion time
 *                per customer + categories + on-time trend.
 *   • customer → same shape but scoped to their org. HQ chart hidden
 *                (not their concern). Per-customer bar shows per-SITE
 *                (their own sites) so the label still means something.
 *                Passed via the `scope="customer"` prop OR inferred
 *                from the JWT — the server returns `scope` so we don't
 *                have to duplicate the decision on the client.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { Activity, Clock, CheckCircle2, Home, TrendingUp } from 'lucide-react';
import { getSitePerformance } from '../services/api';
import { formatDate } from '../utils/date';
import { useAuth } from '../context/AuthContext';

const CHART_COLOURS = [
  '#1d4ed8', '#0891b2', '#f59e0b', '#dc2626', '#8b5cf6',
  '#059669', '#f97316', '#0284c7', '#e11d48', '#7c3aed',
  '#65a30d', '#d97706',
];

const fmtHours = (h) => `${Number(h || 0).toFixed(1)}h`;
const fmtPct = (p) => `${Math.round((Number(p) || 0) * 100)}%`;
const fmtInt = (n) => Number(n || 0).toLocaleString('en-GB');

const shortBucket = (b, period) => {
  if (!b) return b;
  if (period === 'week') return b.replace('W', 'w');
  if (period === 'month') {
    const [y, m] = b.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(m, 10) - 1] || m} ${y.slice(2)}`;
  }
  return b;
};

const Kpi = ({ icon: Icon, label, value, hint, tone = 'slate', to = null, testId = '' }) => {
  const tones = {
    slate: 'text-slate-900 bg-slate-100',
    blue: 'text-blue-700 bg-blue-100',
    green: 'text-emerald-700 bg-emerald-100',
    amber: 'text-amber-700 bg-amber-100',
    red: 'text-red-700 bg-red-100',
  };
  const body = (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <div className={`h-7 w-7 rounded flex items-center justify-center ${tones[tone] || tones.slate}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="text-2xl font-display text-slate-900">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId || undefined}
        className="bg-white border border-slate-200 rounded-md p-4 block hover:border-slate-400 hover:shadow-sm transition-all"
      >
        {body}
      </Link>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testId || undefined}>
      {body}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testId}>
    <div className="mb-3">
      <div className="font-display uppercase tracking-wide text-sm text-slate-900">{title}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
    <div className="h-64">{children}</div>
  </div>
);

export const SitePerformance = ({ variant = 'admin', customerId = '' }) => {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSitePerformance(period, customerId)
      .then(d => { if (!cancelled) { setData(d); setError(null); } })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || 'Could not load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, customerId]);

  const isCustomer = variant === 'customer' || data?.scope === 'customer';
  // Only the customer's OWN portal gets clickable KPI drilldowns —
  // admins previewing a customer's charts on CustomerDetail must NOT
  // navigate into `/customer/*` routes (they'd bounce off the private
  // route guard). Gate on the logged-in user role, not the variant.
  const kpiLinksActive = user?.role === 'customer';
  // Admin KPI drilldowns — go to the admin equivalents. When rendered
  // on a specific customer's detail page (`customerId` prop set), we
  // append the customerId query param so the WO destination stays
  // pre-filtered to that customer. Otherwise (admin overview) the
  // links drop the admin into the global list.
  const adminLinksActive = user?.role === 'admin';
  const adminTimesheetsHref = '/admin/timesheets';
  const adminWosOpenedHref = customerId
    ? `/admin/work-orders?customerId=${customerId}`
    : '/admin/work-orders';
  const adminWosCompletedHref = customerId
    ? `/admin/work-orders?customerId=${customerId}&status=closed`
    : '/admin/work-orders?status=closed';
  // Labels shift a bit for the customer variant so nothing reads "per
  // customer" (they are the customer).
  const perLabel = isCustomer ? 'Hours per site' : 'Hours per customer';
  const perSubtitle = isCustomer
    ? 'Total time spent on each of your sites'
    : 'Total time spent on site (top 12)';
  const avgLabel = isCustomer ? 'Avg WO completion time by site' : 'Avg WO completion time';
  const avgSubtitle = isCustomer
    ? 'Days open → complete, per site'
    : 'Days from open to complete, by customer (fastest first)';

  const timeOnSite = useMemo(() => (data?.timeOnSite || []).map(r => ({
    ...r, label: shortBucket(r.bucket, period),
  })), [data, period]);
  const hqHours = useMemo(() => (data?.hqHours || []).map(r => ({
    ...r, label: shortBucket(r.bucket, period),
  })), [data, period]);
  const throughput = useMemo(() => (data?.throughput || []).map(r => ({
    ...r, label: shortBucket(r.bucket, period),
  })), [data, period]);
  const onTime = useMemo(() => (data?.onTime || []).map(r => ({
    ...r, label: shortBucket(r.bucket, period),
  })), [data, period]);

  return (
    <div className="space-y-4" data-testid={isCustomer ? 'customer-site-performance' : 'site-performance-dashboard'}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display uppercase tracking-wide text-slate-900">
            {isCustomer ? 'How are we doing at your sites?' : 'How are we doing on site?'}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {data?.range
              ? `Data from ${formatDate(data.range.from)} to ${formatDate(data.range.to)}`
              : 'Live from time clock and work orders'}
          </p>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden">
          {['week', 'month', 'year'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              data-testid={`period-${p}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                period === p ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {p[0].toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500 text-sm bg-white border border-slate-200 rounded-md">Loading analytics…</div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-sm rounded-md">{error}</div>
      ) : (
        <>
          {/* KPIs — layout tightens to 4 cols for customers (no HQ tile). */}
          <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-${isCustomer ? 4 : 5} gap-3`}>
            <Kpi
              icon={Clock}
              label={isCustomer ? 'Hours at your sites' : 'Hours on site'}
              value={fmtHours(data.summary.siteHours)}
              tone="blue"
              to={kpiLinksActive
                ? '/customer/timesheet'
                : (adminLinksActive ? adminTimesheetsHref : null)}
              testId={
                isCustomer ? 'kpi-hours-at-sites' : 'kpi-admin-hours-on-site'
              }
            />
            {!isCustomer && (
              <Kpi
                icon={Home}
                label="Hours at HQ"
                value={fmtHours(data.summary.hqHours)}
                tone="slate"
                hint="Yard / workshop time"
                to={adminLinksActive ? adminTimesheetsHref : null}
                testId="kpi-admin-hours-at-hq"
              />
            )}
            <Kpi
              icon={Activity}
              label="WOs opened"
              value={fmtInt(data.summary.wosOpened)}
              tone="slate"
              to={kpiLinksActive
                ? '/customer/work-orders?status=all'
                : (adminLinksActive ? adminWosOpenedHref : null)}
              testId={isCustomer ? 'kpi-wos-opened' : 'kpi-admin-wos-opened'}
            />
            <Kpi
              icon={CheckCircle2}
              label="WOs completed"
              value={fmtInt(data.summary.wosCompleted)}
              tone="green"
              to={kpiLinksActive
                ? '/customer/work-orders?status=completed'
                : (adminLinksActive ? adminWosCompletedHref : null)}
              testId={isCustomer ? 'kpi-wos-completed' : 'kpi-admin-wos-completed'}
            />
            <Kpi
              icon={TrendingUp}
              label="On-time rate"
              value={fmtPct(data.summary.onTimeRate)}
              hint="Completed vs due date"
              tone={data.summary.onTimeRate >= 0.9 ? 'green' : data.summary.onTimeRate >= 0.7 ? 'amber' : 'red'}
            />
          </div>

          {/* Row: time on site + WO throughput */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title={isCustomer ? 'Time on your sites' : 'Time on site'}
              subtitle="Hours logged per bucket. HQ / yard time is excluded."
              testId="chart-time-on-site"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeOnSite} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmtHours(v)} />
                  <Bar dataKey="hours" fill={CHART_COLOURS[0]} name="Hours" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Work-order throughput"
              subtitle="Opened vs completed per bucket"
              testId="chart-wo-throughput"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={throughput} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="opened" fill={CHART_COLOURS[0]} name="Opened" />
                  <Bar dataKey="completed" fill={CHART_COLOURS[5]} name="Completed" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Row: hours per customer/site + avg completion time */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title={perLabel} subtitle={perSubtitle} testId="chart-hours-per-customer">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.timePerCustomer} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="customer" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip formatter={(v) => fmtHours(v)} />
                  <Bar dataKey="hours" fill={CHART_COLOURS[1]}>
                    {data.timePerCustomer.map((_, i) => (
                      <Cell key={i} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title={avgLabel} subtitle={avgSubtitle} testId="chart-avg-duration">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.avgDurationByCustomer} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="customer" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip formatter={(v, _n, p) => [`${v} days`, `Sample: ${p.payload.sample} WOs`]} />
                  <Bar dataKey="avgDays" fill={CHART_COLOURS[3]}>
                    {data.avgDurationByCustomer.map((_, i) => (
                      <Cell key={i} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Row: category breakdown (donut) + on-time trend */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              title="Work-order categories"
              subtitle={isCustomer ? 'Your live work orders by type' : 'Breakdown of all live work orders'}
              testId="chart-category-breakdown"
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.categoryBreakdown}
                    dataKey="count"
                    nameKey="category"
                    cx="40%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {data.categoryBreakdown.map((_, i) => (
                      <Cell key={i} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="On-time vs late completions"
              subtitle="How many WOs closed by their due date"
              testId="chart-on-time"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={onTime} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="onTime" stackId="ot" fill={CHART_COLOURS[5]} name="On time" />
                  <Bar dataKey="late" stackId="ot" fill={CHART_COLOURS[3]} name="Late" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Admin-only: separate HQ chart */}
          {!isCustomer && hqHours.length > 0 && (
            <ChartCard
              title="Time at Lutrom HQ"
              subtitle="Yard / workshop hours — everything clocked in without a customer link"
              testId="chart-hq-hours"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hqHours} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => fmtHours(v)} />
                  <Bar dataKey="hours" fill={CHART_COLOURS[2]} name="HQ hours" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </>
      )}
    </div>
  );
};

export default SitePerformance;
